import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { resolveProductListings } from "@/lib/product-listing-api";
import {
  SSE_FALLBACK_POLL_MS,
  SSE_SAFETY_POLL_MS,
  subscribeToSseInvalidation,
} from "@/lib/sse-invalidation";
import {
  isProductSyncEvent,
  matchesProductIssue,
  type ProductInventoryGate,
  retriedSelectionIssues,
  selectionValidationIssues,
} from "./product-listing-helpers";

interface UseSelectionValidationOptions {
  shopId: string;
  adProduct?: "sp";
  inventory: ProductInventoryGate;
  selected: SelectedProduct[];
  validateSelection: boolean;
  invalidProducts: CampaignCreationInvalidProduct[];
  onInvalidProductsChange: (products: CampaignCreationInvalidProduct[]) => void;
  refreshStreamFailed: boolean;
  mounted: MutableRefObject<boolean>;
  selectedRef: MutableRefObject<SelectedProduct[]>;
  selectionRevision: MutableRefObject<{ identity: string; revision: number }>;
}

/** Re-checks already selected products against the live catalog and exposes their issues. */
export function useSelectionValidation({
  shopId,
  adProduct,
  inventory,
  selected,
  validateSelection,
  invalidProducts,
  onInvalidProductsChange,
  refreshStreamFailed,
  mounted,
  selectedRef,
  selectionRevision,
}: UseSelectionValidationOptions) {
  const { t } = useTranslation("campaignCreation");
  const queryClient = useQueryClient();
  const selectionValidationQueryKey = useMemo(
    () =>
      [
        "product-listings",
        "selected-validation",
        shopId,
        adProduct,
        selected.map(({ listingKey, asin }) => ({ listingKey, asin })),
      ] as const,
    [adProduct, selected, shopId],
  );
  const selectionValidation = useQuery({
    queryKey: selectionValidationQueryKey,
    queryFn: () =>
      resolveProductListings(
        shopId,
        selected.map(({ listingKey }) => listingKey),
        adProduct,
      ),
    enabled: validateSelection && selected.length > 0,
    staleTime: 0,
    retry: false,
    refetchInterval: refreshStreamFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });
  useEffect(() => {
    if (!shopId || !validateSelection || selected.length === 0) return;
    return subscribeToSseInvalidation(queryClient, {
      id: `shop-sync:${shopId}`,
      url: `/api/v1/sync/shops/${encodeURIComponent(shopId)}/events`,
      eventNames: ["run.changed"],
      queryKey: selectionValidationQueryKey,
      shouldInvalidate: (event) => isProductSyncEvent(event),
    });
  }, [queryClient, selected.length, selectionValidationQueryKey, shopId, validateSelection]);
  const selectionIssues = useMemo(() => {
    if (!validateSelection)
      return invalidProducts.filter((issue) =>
        selected.some((product) => matchesProductIssue(issue, product)),
      );
    if (!selectionValidation.data) return [];
    return selectionValidationIssues(selected, selectionValidation.data, adProduct, t);
  }, [adProduct, invalidProducts, selected, selectionValidation.data, t, validateSelection]);
  const [retryPending, setRetryPending] = useState(false);
  const retryInFlight = useRef(false);
  async function retrySelection() {
    if (retryInFlight.current || selectedRef.current.length === 0) return;
    retryInFlight.current = true;
    setRetryPending(true);
    const requested = selectedRef.current;
    const requestedRevision = selectionRevision.current.revision;
    const isCurrent = () =>
      mounted.current && requestedRevision === selectionRevision.current.revision;
    try {
      const [result] = await Promise.allSettled([
        resolveProductListings(
          shopId,
          requested.map(({ listingKey }) => listingKey),
          adProduct,
        ),
        new Promise<void>((resolve) => window.setTimeout(resolve, 500)),
      ]);
      if (!isCurrent()) return;
      if (result.status === "rejected") throw result.reason;
      const response = result.value;
      const issues = retriedSelectionIssues(requested, response, adProduct, inventory);
      onInvalidProductsChange(issues);
      queryClient.setQueryData(selectionValidationQueryKey, response);
      if (issues.length === 0) toast.success(t("products.selectionCheck.success"));
    } catch {
      if (isCurrent()) toast.error(t("products.selectionCheck.failed"));
    } finally {
      retryInFlight.current = false;
      if (mounted.current) setRetryPending(false);
    }
  }

  const selectionValidationIncomplete =
    adProduct === "sp" &&
    selectionValidation.data?.results.some(
      (result) =>
        "listing" in result &&
        result.listing.isActive &&
        result.listing.listingRole !== "parent" &&
        result.listing.eligibility === "unknown",
    );
  const selectionUnverified =
    validateSelection &&
    selected.length > 0 &&
    (selectionValidation.isPending ||
      selectionValidation.isError ||
      Boolean(selectionValidationIncomplete));

  return {
    selectionValidation,
    selectionIssues,
    selectionValidationIncomplete,
    selectionUnverified,
    retryPending: retryPending || selectionValidation.isFetching,
    retrySelection,
  };
}
